import { useCallback } from "react"

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type {
  CreateDashboardPayload,
  SavedDashboard,
  UpdateDashboardPayload,
} from "../services/dashboard-service"

import { queryKeys } from "@/lib/query-keys"

import { DashboardService } from "../services/dashboard-service"

const QUERY_KEY = queryKeys.dashboards
const DASHBOARD_STALE_TIME = 1000 * 60 * 2

export function mergeDashboardEnvelope(
  current: SavedDashboard | undefined,
  updated: SavedDashboard
): SavedDashboard {
  // PUT responses can be a storage-shaped document while list/get responses
  // are enriched with role, sharing and invitation metadata. Preserve fields
  // omitted by the mutation response until the invalidation fetch completes.
  // Dashboard PUTs do not mutate these list/get enrichment fields, and the
  // storage-shaped response may contain false/null defaults for them. Prefer
  // the current enriched values whenever they are known.
  if (!current) return updated

  return {
    ...current,
    ...updated,
    ...(current.is_favorite !== undefined ? { is_favorite: current.is_favorite } : {}),
    ...(current.ownerUserId !== undefined ? { ownerUserId: current.ownerUserId } : {}),
    ...(current.participants !== undefined ? { participants: current.participants } : {}),
    ...(current.currentUserRole !== undefined ? { currentUserRole: current.currentUserRole } : {}),
    ...(current.hasUnviewedNotification !== undefined
      ? { hasUnviewedNotification: current.hasUnviewedNotification }
      : {}),
    ...(current.invitationNotification !== undefined
      ? { invitationNotification: current.invitationNotification }
      : {}),
  }
}

export function useDashboards() {
  return useQuery<SavedDashboard[]>({
    queryKey: QUERY_KEY,
    queryFn: () => DashboardService.listDashboards(),
    staleTime: DASHBOARD_STALE_TIME,
  })
}

export function useDashboard(dashboardId: string | undefined) {
  return useQuery<SavedDashboard>({
    queryKey: queryKeys.dashboard(dashboardId!),
    queryFn: () => DashboardService.getDashboard(dashboardId!),
    enabled: !!dashboardId,
    staleTime: DASHBOARD_STALE_TIME,
  })
}

export function useCreateDashboard() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: CreateDashboardPayload) => DashboardService.createDashboard(payload),
    onSuccess: (created) => {
      queryClient.setQueryData<SavedDashboard[]>(QUERY_KEY, (current) => [
        created,
        ...(current ?? []).filter((dashboard) => dashboard.id !== created.id),
      ])
      queryClient.setQueryData(queryKeys.dashboard(created.id), created)
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    },
    onError: () => {
      toast.error(t("dash.dashboard.create_error"))
    },
  })
}

export function useUpdateDashboard() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      dashboardId,
      payload,
    }: {
      dashboardId: string
      payload: UpdateDashboardPayload
    }) => DashboardService.updateDashboard(dashboardId, payload),
    onSuccess: (updated) => {
      queryClient.setQueryData<SavedDashboard[]>(QUERY_KEY, (current) =>
        current?.map((dashboard) =>
          dashboard.id === updated.id ? mergeDashboardEnvelope(dashboard, updated) : dashboard
        )
      )
      queryClient.setQueryData<SavedDashboard>(queryKeys.dashboard(updated.id), (current) =>
        mergeDashboardEnvelope(current, updated)
      )
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    },
    onError: () => {
      toast.error(t("dash.dashboard.update_error"))
    },
  })
}

export function useDeleteDashboard() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ dashboardId, type }: { dashboardId: string; type?: "personal" | "company" }) =>
      DashboardService.deleteDashboard(dashboardId, type),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    },
    onError: () => {
      toast.error(t("dash.dashboard.delete_error"))
    },
  })
}

export function useToggleDashboardFavorite() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (dashboardId: string) => DashboardService.toggleFavorite(dashboardId),
    onMutate: async (dashboardId) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEY })
      const prev = queryClient.getQueryData<SavedDashboard[]>(QUERY_KEY)
      queryClient.setQueryData<SavedDashboard[]>(QUERY_KEY, (old) =>
        old?.map((d) => (d.id === dashboardId ? { ...d, is_favorite: !d.is_favorite } : d))
      )
      return { prev }
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(QUERY_KEY, ctx.prev)
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    },
  })
}

export function usePrefetchDashboard() {
  const queryClient = useQueryClient()

  return useCallback(
    (dashboardId: string) => {
      const queryKey = queryKeys.dashboard(dashboardId)
      if (!queryClient.getQueryData(queryKey)) {
        void queryClient.prefetchQuery({
          queryKey,
          queryFn: () => DashboardService.getDashboard(dashboardId),
          staleTime: DASHBOARD_STALE_TIME,
        })
      }
    },
    [queryClient]
  )
}
